import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { LockKey, Copy, Check, Warning, Eye } from '@phosphor-icons/react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { useAuth } from '../../contexts/AuthContext';
import {
  isSupported, encryptCapsule, buildCode, parseCode, decryptCapsule, isPlausibleKey,
  makeVerifier, checkVerifier,
} from '../../lib/capsuleCrypto';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ---------------- Compose (sender) ---------------- */

/**
 * `autoOpen` opens compose straight away -- Room's Secure Capsule door lands
 * here. `onCancel` runs only when compose is closed without sending, which is
 * how that door's Cancel goes back to Room; once a capsule exists the member
 * stays in Support, where it now is.
 */
export const CapsuleComposeButton = ({ chatId, autoOpen = false, onCancel }) => {
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState('compose'); // compose | code
  const [text, setText] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const reset = () => { setStage('compose'); setText(''); setCode(''); setCopied(false); };

  const onOpenChange = (o) => {
    setOpen(o);
    if (!o) {
      if (stage === 'compose' && onCancel) onCancel();
      reset();
    }
  };

  useEffect(() => {
    if (autoOpen) setOpen(true);
  }, [autoOpen]);

  const create = async () => {
    if (!text.trim()) return;
    if (!isSupported()) {
      toast.error('Secure Capsule needs a modern browser over HTTPS');
      return;
    }
    setBusy(true);
    try {
      const { iv, ciphertext, keyB64 } = await encryptCapsule(text.trim());
      // Travels with the capsule so the recipient can test their code without
      // spending it. See makeVerifier.
      const verifier = await makeVerifier(keyB64);
      const { data } = await axios.post(
        `${API_URL}/api/capsules`,
        {
          chat_id: chatId, iv, ciphertext,
          verifier_iv: verifier.iv, verifier_ciphertext: verifier.ciphertext,
        },
        { withCredentials: true }
      );
      setCode(buildCode(data.capsule_id, keyB64));
      setText(''); // drop plaintext from memory
      setStage('code');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create capsule');
    } finally {
      setBusy(false);
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { /* clipboard denied */ }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button type="button" variant="ghost" size="touch-icon" className="shrink-0" title="Send a Secure Capsule" aria-label="Send a Secure Capsule" data-testid="capsule-button">
          <LockKey className="w-5 h-5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Secure Capsule</DialogTitle>
          <DialogDescription>
            A one-time message encrypted on your device. The key is never uploaded — you share it separately.
          </DialogDescription>
          <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground" data-testid="capsule-rules">
            Opens once · Expires after 7 days · Text only
          </p>
        </DialogHeader>

        {stage === 'compose' && (
          <div className="space-y-4" data-testid="capsule-compose">
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Write the secret message…"
              className="min-h-[120px]"
              autoFocus
              data-testid="capsule-text"
            />
            <div className="flex items-start gap-2 text-xs text-muted-foreground">
              <Warning className="w-4 h-4 mt-0.5 shrink-0" />
              <span>A capsule can be opened once and cannot prevent the recipient from keeping a copy. It protects the text in transit and storage — not after it is read.</span>
            </div>
            <Button onClick={create} disabled={busy || !text.trim()} className="w-full bg-primary hover:bg-primary/90" data-testid="capsule-create">
              {busy ? 'Encrypting…' : 'Encrypt & send'}
            </Button>
          </div>
        )}

        {stage === 'code' && (
          <div className="space-y-4" data-testid="capsule-code">
            <div className="rounded-lg border border-secondary/40 bg-muted/40 p-3">
              <p className="text-sm font-medium text-foreground mb-1">Share this code with the recipient</p>
              <p className="text-xs text-muted-foreground mb-3">
                Share this code through another channel. Arcanum cannot read or recover this message.
                In person, SMS or a call will do. Anyone with this code and access to the thread can
                open the capsule once. It won't be shown again.
              </p>
              <p className="font-mono text-xs break-all bg-background rounded p-2 border border-border" data-testid="capsule-code-value">
                {code}
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={copyCode} data-testid="capsule-copy">
                {copied ? <><Check className="w-4 h-4 mr-2" /> Copied</> : <><Copy className="w-4 h-4 mr-2" /> Copy code</>}
              </Button>
              <Button className="flex-1 bg-primary hover:bg-primary/90" onClick={() => onOpenChange(false)}>
                Done
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

/* ---------------- Capsule bubble (recipient/sender) ---------------- */

export const CapsuleMessage = ({ message }) => {
  const { user } = useAuth();
  const isSent = message.sender_id === user?.id;
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [plaintext, setPlaintext] = useState(null);
  const [burned, setBurned] = useState(!!message.capsule_burned);

  const openCapsule = async (e) => {
    e.preventDefault();

    // Accept a full "capsule:v1:id:key" code or just the raw key.
    const parsed = parseCode(code);
    const capsuleId = parsed?.capsuleId || message.capsule_id;
    const keyB64 = parsed?.keyB64 || code.trim();

    // Everything checkable is checked BEFORE the server is asked, because
    // asking now costs the capsule: the claim destroys the ciphertext in the
    // same operation that returns it. A typo must not be what erases the
    // message. What survives this guard is a well-formed key for the right
    // capsule, which is wrong only if someone was handed the wrong code.
    if (parsed && parsed.capsuleId !== message.capsule_id) {
      toast.error('That code belongs to a different capsule');
      return;
    }
    if (!isPlausibleKey(keyB64)) {
      toast.error('That does not look like a capsule code');
      return;
    }

    setBusy(true);
    try {
      // Prove the key BEFORE spending the capsule. The claim destroys the
      // ciphertext in the same operation that returns it, and a mistyped
      // character does not change a key's length -- so no structural check
      // catches it, and without this one typo would erase the message.
      let fits = null;   // null: nothing to check against
      try {
        const { data: v } = await axios.get(
          `${API_URL}/api/capsules/${capsuleId}/verifier`, { withCredentials: true },
        );
        fits = await checkVerifier(keyB64, v.iv, v.ciphertext);
      } catch (err) {
        fits = null;   // a capsule made before verifiers existed
      }
      if (fits === false) {
        toast.error('That code does not open this capsule. It has not been used up — check the code and try again.');
        return;
      }

      const { data } = await axios.post(
        `${API_URL}/api/capsules/${capsuleId}/claim`, {}, { withCredentials: true },
      );
      // From here the ciphertext no longer exists server-side, so the bubble
      // flips whether or not the key turns out to fit.
      setBurned(true);
      try {
        setPlaintext(await decryptCapsule(keyB64, data.iv, data.ciphertext));
      } catch (err) {
        toast.error('The capsule opened, but that key did not fit it. It cannot be opened again.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'This capsule is no longer available');
    } finally {
      setBusy(false);
    }
  };

  const onOpenChange = (o) => {
    setOpen(o);
    if (!o) { setCode(''); setPlaintext(null); }
  };

  return (
    <>
      <div
        className={`rounded-2xl px-4 py-3 border ${isSent ? 'border-primary/40 bg-primary/5' : 'border-secondary/40 bg-muted/40'}`}
        data-testid={`capsule-bubble-${message.id}`}
      >
        <div className="flex items-center gap-2">
          <LockKey className="w-4 h-4" style={{ color: 'hsl(var(--gold))' }} weight="fill" />
          <span className="text-sm font-medium">Secure Capsule</span>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          {burned ? 'Opened — no longer available' : 'One-time encrypted message'}
        </p>
        {!burned && (
          <Button size="sm" variant="outline" className="mt-2 h-8" onClick={() => setOpen(true)} data-testid={`capsule-open-${message.id}`}>
            <Eye className="w-3.5 h-3.5 mr-1.5" /> Open
          </Button>
        )}
      </div>

      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Open Secure Capsule</DialogTitle>
          </DialogHeader>

          {plaintext === null ? (
            <form onSubmit={openCapsule} className="space-y-4" data-testid="capsule-open-form">
              <p className="text-sm text-muted-foreground">
                Enter the code the sender gave you. A wrong code is safe to retry; the capsule is used up only once the right one opens it.
              </p>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="capsule:v1:…"
                required
                autoFocus
                className="font-mono text-xs"
                data-testid="capsule-open-code"
              />
              <Button type="submit" disabled={busy || !code.trim()} className="w-full bg-primary hover:bg-primary/90" data-testid="capsule-open-submit">
                {busy ? 'Opening…' : 'Open'}
              </Button>
            </form>
          ) : (
            <div className="space-y-3" data-testid="capsule-revealed">
              <div className="rounded-lg border border-border bg-background p-3">
                <p className="text-sm whitespace-pre-wrap break-words">{plaintext}</p>
              </div>
              <div className="flex items-start gap-2 text-xs text-muted-foreground">
                <Warning className="w-4 h-4 mt-0.5 shrink-0" />
                <span>This capsule has been destroyed and won't be shown again. Copy it now if you need to keep it.</span>
              </div>
              <Button className="w-full bg-primary hover:bg-primary/90" onClick={() => onOpenChange(false)}>Close</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
